"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CRON_EXPRESSIONS = exports.CRON_PRESET_VALUES = exports.PROCESS_CRON_PRESET_VALUES = exports.AUTOMATION_MIN_TICK_MS = void 0;
exports.isCronPreset = isCronPreset;
exports.isProcessCronPreset = isProcessCronPreset;
exports.nightlyMinute = nightlyMinute;
exports.cronExpressionForPreset = cronExpressionForPreset;
exports.nextScheduledRun = nextScheduledRun;
exports.timezoneOffsetMs = timezoneOffsetMs;
const database_identity_1 = require("@streamlain/database-identity");
/**
 * Smallest interval of any automation trigger (card dec-am-spec-approve-1029):
 * schedules start at `every_1_min`, and row triggers that collect new rows
 * run after one minute at the latest.
 */
exports.AUTOMATION_MIN_TICK_MS = 60_000;
/** Presets the process UI offers. */
exports.PROCESS_CRON_PRESET_VALUES = [
    'every_1_min',
    'every_5_min',
    'every_15_min',
    'every_30_min',
    'hourly',
    'daily_9am',
    'daily_6pm',
    'weekly_monday_9am',
    'monthly_first_9am',
];
/** All presets; nightly only for cleanup. */
exports.CRON_PRESET_VALUES = [
    'nightly',
    ...exports.PROCESS_CRON_PRESET_VALUES,
];
function isCronPreset(value) {
    return exports.CRON_PRESET_VALUES.includes(value);
}
function isProcessCronPreset(value) {
    return exports.PROCESS_CRON_PRESET_VALUES.includes(value);
}
/** Cron expressions for each preset (DRY source of truth). */
exports.CRON_EXPRESSIONS = {
    every_1_min: '*/1 * * * *',
    every_5_min: '*/5 * * * *',
    every_15_min: '*/15 * * * *',
    every_30_min: '*/30 * * * *',
    hourly: '0 * * * *',
    daily_9am: '0 9 * * *',
    daily_6pm: '0 18 * * *',
    weekly_monday_9am: '0 9 * * 1',
    monthly_first_9am: '0 9 1 * *',
};
/** Stable across processes, replicas and runs; no new scheduling state. */
function nightlyMinute(configId) {
    if (!configId?.trim())
        throw new Error('NIGHTLY_CONFIG_ID_REQUIRED');
    let hash = 2166136261;
    for (let i = 0; i < configId.length; i++) {
        hash = Math.imul(hash ^ configId.charCodeAt(i), 16777619) >>> 0;
    }
    return 120 + (hash % 120);
}
function cronExpressionForPreset(preset, configId) {
    if (preset !== 'nightly')
        return exports.CRON_EXPRESSIONS[preset];
    const minute = nightlyMinute(configId ?? '');
    return `${minute % 60} ${Math.floor(minute / 60)} * * *`;
}
/**
 * The next run of a preset after `now` in the timezone; the platform
 * scheduler and the standalone StreamKit both schedule with it.
 *
 * Interval presets (every_X_min, hourly) step on epoch milliseconds and do
 * not use the timezone. For daily/weekly/monthly presets the hour is
 * interpreted in the target timezone with the offset of the target day,
 * independent of the process timezone.
 */
function nextScheduledRun(preset, timezone, options) {
    const now = options?.now ?? new Date();
    switch (preset) {
        case 'nightly':
            return nextNightly(now, timezone, options?.configId ?? '');
        case 'every_1_min':
            return nextInterval(now, 1);
        case 'every_5_min':
            return nextInterval(now, 5);
        case 'every_15_min':
            return nextInterval(now, 15);
        case 'every_30_min':
            return nextInterval(now, 30);
        case 'hourly':
            return nextInterval(now, 60);
        case 'daily_9am':
            return nextDailyTime(now, 9, 0, timezone);
        case 'daily_6pm':
            return nextDailyTime(now, 18, 0, timezone);
        case 'weekly_monday_9am':
            return nextWeeklyTime(now, 1, 9, 0, timezone);
        case 'monthly_first_9am':
            return nextMonthlyTime(now, 1, 9, 0, timezone);
        default: {
            const unknown = preset;
            throw new Error(`UNKNOWN_CRON_PRESET: ${String(unknown)}`);
        }
    }
}
/** The UTC offset of the timezone at the given instant, in milliseconds. */
function timezoneOffsetMs(date, timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'longOffset',
    });
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(p => p.type === 'timeZoneName')?.value || '';
    if (timeZonePart === 'GMT')
        return 0;
    // Parse offset like "GMT+01:00" or "GMT-05:00"
    const match = timeZonePart.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!match) {
        throw new Error(`TIMEZONE_OFFSET_UNRESOLVED: ${timezone}`);
    }
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes) * 60 * 1000;
}
function nextNightly(now, timezone, configId) {
    const minute = nightlyMinute(configId);
    if (!Number.isFinite(now.getTime()))
        throw new Error('INVALID_SCHEDULE_TIME');
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const localParts = (date) => Object.fromEntries(formatter.formatToParts(date).map(part => [part.type, Number(part.value)]));
    const today = localParts(now);
    const midnight = Date.UTC(today.year, today.month - 1, today.day);
    for (let day = 0; day < 3; day++) {
        const localDay = midnight + day * 86_400_000;
        const expected = new Date(localDay);
        // Probe both sides of an offset transition. Repeated local times choose
        // their first UTC occurrence, even when 'now' is in the repeated hour.
        const offsets = new Set([-1, 0, 1].map(delta => timezoneOffsetMs(new Date(localDay + delta * 86_400_000), timezone)));
        for (let shifted = minute; shifted < 240; shifted += 60) {
            const localTarget = localDay + shifted * 60_000;
            const candidates = [...offsets]
                .map(offset => localTarget - offset)
                .filter(timestamp => {
                const parts = localParts(new Date(timestamp));
                return (parts.year === expected.getUTCFullYear() &&
                    parts.month === expected.getUTCMonth() + 1 &&
                    parts.day === expected.getUTCDate() &&
                    parts.hour === Math.floor(shifted / 60) &&
                    parts.minute === shifted % 60);
            });
            if (!candidates.length)
                continue;
            const first = Math.min(...candidates);
            if (first > now.getTime())
                return new Date(first);
            break; // This local night's one assigned occurrence is already past.
        }
    }
    throw new Error('NIGHTLY_LOCAL_TIME_UNRESOLVED');
}
/**
 * Next interval time (e.g., every 5 minutes aligned to clock).
 *
 * Computed on epoch milliseconds so the step stays exact across DST
 * transitions; local wall-clock arithmetic skips the repeated hour when
 * clocks fall back and jumps over the missing hour when they spring forward.
 */
function nextInterval(now, intervalMinutes) {
    const intervalMs = intervalMinutes * 60 * 1000;
    return new Date(Math.floor(now.getTime() / intervalMs) * intervalMs + intervalMs);
}
function nextDailyTime(now, hour, minute, timezone) {
    const today = zonedDay(now, timezone);
    const next = atZonedTime(today, 0, hour, minute, timezone);
    return next.getTime() > now.getTime()
        ? next
        : atZonedTime(today, 1, hour, minute, timezone);
}
function nextWeeklyTime(now, dayOfWeek, // 0 = Sunday, 1 = Monday, etc.
hour, minute, timezone) {
    const today = zonedDay(now, timezone);
    const daysUntilTarget = (dayOfWeek - today.getUTCDay() + 7) % 7;
    const next = atZonedTime(today, daysUntilTarget, hour, minute, timezone);
    return next.getTime() > now.getTime()
        ? next
        : atZonedTime(today, daysUntilTarget + 7, hour, minute, timezone);
}
function nextMonthlyTime(now, dayOfMonth, hour, minute, timezone) {
    const today = zonedDay(now, timezone);
    const thisMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), dayOfMonth));
    const next = atZonedTime(thisMonth, 0, hour, minute, timezone);
    if (next.getTime() > now.getTime())
        return next;
    const nextMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, dayOfMonth));
    return atZonedTime(nextMonth, 0, hour, minute, timezone);
}
/** The calendar day of `now` in the timezone, as UTC midnight of that date. */
function zonedDay(now, timezone) {
    const wall = (0, database_identity_1.zonedDateTimeParts)(now, timezone);
    return new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
}
/**
 * The instant of hour:minute in the timezone, `days` after the given day,
 * with the offset of that target day (exact across DST transitions).
 */
function atZonedTime(day, days, hour, minute, timezone) {
    const target = new Date(day.getTime() + days * 86_400_000);
    return (0, database_identity_1.zonedWallDateTimeToDate)({
        year: target.getUTCFullYear(),
        month: target.getUTCMonth() + 1,
        day: target.getUTCDate(),
        hour,
        minute,
        second: 0,
        millisecond: 0,
    }, timezone);
}
//# sourceMappingURL=schedule.js.map